/*
  The Osmo look in the player: footage runs through the Film Emulation LUT for the project's look and
  its camera (public/luts/<look>/<camera>.bin, made by scripts/make-luts.mjs from the osmo.inc film
  engine, color only). The camera: the team's choice for that letter, else the clip's own metadata
  when it says log. No look, or a camera that isn't log, plays as shot. rec709 is the plain one: the
  camera's log as standard Rec.709 video (scripts/make-rec709.mjs), no film, so never filmic.
*/
import looks from '../looks.json'
import type { Media, Project } from '../types'

export const LUT_SIZE = looks.size
export const PROFILES = looks.cameras
export const LOOKS = looks.looks
export const profileLabel = (key: string | null | undefined) => PROFILES.find((p) => p.key === key)?.label
export const lookLabel = (key: string | null | undefined) => LOOKS.find((l) => l.key === key)?.label
/** The look can be Filmic (grain and halation from its film): every film look, not plain Rec.709. */
export const filmicLook = (key: string | null | undefined) => !!key && LOOKS.some((l) => l.key === key && !('film' in l && l.film === false))

export const lookCams = (p: Project | undefined): Record<string, string> => {
  try { return JSON.parse(p?.look_cams || '{}') } catch { return {} }
}

/** The camera profile a clip on camera `label` is treated as, or null when it plays as shot. */
export function cameraFor(project: Project | undefined, media: Media | undefined, label: string): string | null {
  if (!media) return null
  const pick = lookCams(project)[label.replace(/\d+$/, '')]
  if (pick) return pick === 'none' ? null : pick
  return media.profile && media.profile !== 'none' ? media.profile : null
}

/** The LUT a clip on camera `label` plays through ("500t/slog3-cine"), or null to show it as shot. */
export function lutFor(project: Project | undefined, media: Media | undefined, label: string): string | null {
  const cam = project?.look ? cameraFor(project, media, label) : null
  return cam ? `${project!.look}/${cam}` : null
}

const cache = new Map<string, Promise<Uint8Array>>()
export function loadLut(key: string): Promise<Uint8Array> {
  let hit = cache.get(key)
  if (!hit) {
    hit = fetch(`/luts/${key}.bin`).then(async (r) => {
      if (!r.ok) throw new Error('LUT ' + r.status)
      const b = new Uint8Array(await r.arrayBuffer())
      if (b.length !== LUT_SIZE ** 3 * 3) throw new Error('LUT size')
      return b
    })
    hit.catch(() => cache.delete(key))
    cache.set(key, hit)
  }
  return hit
}
